import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, Plus, MessageSquare } from 'lucide-react';
import { ordersAPI, listingsAPI } from '../utils/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';

const STATUS_COLORS = {
  waiting: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  pending: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  confirmed: 'bg-green-500/15 text-green-400 border-green-500/30',
  shipped: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  delivered: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/30',
};
const STATUS_LABELS = {
  waiting: 'Pending', pending: 'Pending',
  confirmed: 'Purchased', purchased: 'Purchased',
  shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled',
};
const SELECTABLE = ['confirmed', 'shipped', 'delivered'];
const PILLS = ['all', 'purchased', 'shipped', 'delivered'];

const Orders = () => {
  const { formatFull } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [listings, setListings] = useState([]);
  const [createForm, setCreateForm] = useState({ customerName: '', customerEmail: '', customerPhone: '', listingId: '', notes: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const allOrders = await ordersAPI.getAll('');
      setOrders(allOrders);
    }
    catch (e) { console.error('Orders load failed:', e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, newStatus) => {
    try { await ordersAPI.updateStatus(id, newStatus); toast.success(`Order → ${STATUS_LABELS[newStatus] || newStatus}`); load(); }
    catch (e) { toast.error(e.message || 'Failed to update status'); }
  };

  const openCreateDialog = async () => {
    try {
      const ls = await listingsAPI.getAll();
      setListings(ls.filter(l => l.status === 'active' || l.status === 'approved'));
    } catch (e) { console.warn('Listings load failed:', e.message); }
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.customerName.trim() || !createForm.listingId) {
      toast.error('Customer name and watch are required'); return;
    }
    setCreating(true);
    try {
      // Find the selected listing details
      const listing = listings.find(l => l.id === createForm.listingId);
      await ordersAPI.create({
        customerName: createForm.customerName.trim(),
        customerEmail: createForm.customerEmail.trim(),
        customerPhone: createForm.customerPhone.trim(),
        listingId: createForm.listingId,
        listingTitle: listing ? `${listing.brand} ${listing.model}` : '',
        totalPrice: listing?.price || 0,
        notes: createForm.notes.trim(),
        source: 'whatsapp',
      });
      toast.success('Order created from WhatsApp inquiry');
      setCreateOpen(false);
      setCreateForm({ customerName: '', customerEmail: '', customerPhone: '', listingId: '', notes: '' });
      load();
    } catch (e) { toast.error(e.message || 'Failed to create order'); }
    finally { setCreating(false); }
  };

  const filtered = orders.filter(o => {
    // Status filter
    if (filter !== 'all') {
      const status = (o.status || '').toLowerCase();
      if (filter === 'purchased' && status !== 'confirmed' && status !== 'purchased') return false;
      if (filter === 'shipped' && status !== 'shipped') return false;
      if (filter === 'delivered' && status !== 'delivered') return false;
    }
    // Search filter
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.orderNumber + o.buyer + o.item).toLowerCase().includes(q);
  });

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-muted-foreground animate-pulse">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Orders</h1>
          <p className="text-muted-foreground mt-1 text-sm">{orders.length} orders</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-primary text-primary-foreground hover:bg-[hsl(42,44%,62%)]">
          <Plus className="h-4 w-4 mr-2" /> Create Order
        </Button>
      </div>

      {/* Filter Pills + Search */}
      <div className="flex items-center gap-2 flex-wrap">
        {PILLS.map(p => (
          <Button key={p} variant={filter === p ? 'default' : 'outline'} size="sm" onClick={() => setFilter(p)}
            className={filter === p ? 'bg-primary text-primary-foreground' : ''}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </Button>
        ))}
        <div className="flex-1" />
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search by ID, customer, item..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
      </div>

      {/* Orders Table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No orders</TableCell></TableRow>
            ) : filtered.map(o => {
              const currentLabel = STATUS_LABELS[o.status] || 'Waiting for Confirmation';
              const currentColor = STATUS_COLORS[o.status] || STATUS_COLORS.waiting;
              const isFinal = o.status === 'delivered' || o.status === 'cancelled';
              return (
                <TableRow key={o.id}>
                  <TableCell className="font-mono text-xs">{o.orderNumber || o.id?.substring(0, 8)}</TableCell>
                  <TableCell>{o.buyer}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{o.item || '—'}</TableCell>
                  <TableCell className="font-medium">{formatFull(o.total)}</TableCell>
                  <TableCell>
                    {isFinal ? (
                      <Badge variant="outline" className={currentColor}>{currentLabel}</Badge>
                    ) : (
                      <Select value={o.status} onValueChange={(val) => handleStatusChange(o.id, val)}>
                        <SelectTrigger className={`h-7 w-[180px] text-xs border ${currentColor} bg-transparent`}>
                          <SelectValue>{currentLabel}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {SELECTABLE.map(s => (
                            <SelectItem key={s} value={s}>
                              <span className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  s === 'confirmed' ? 'bg-green-400'
                                    : s === 'shipped' ? 'bg-purple-400'
                                    : 'bg-emerald-400'
                                }`} />
                                {STATUS_LABELS[s]}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{o.paidAt ? new Date(o.paidAt).toLocaleDateString() : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Create Order Dialog (WhatsApp flow) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-500" />
              Create Order from WhatsApp
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Record an order from a WhatsApp inquiry. Fill in customer details and select the watch.
          </p>
          <form onSubmit={handleCreate} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Customer Name *</Label>
                <Input placeholder="John Doe" value={createForm.customerName} onChange={e => setCreateForm(f => ({ ...f, customerName: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone / WhatsApp</Label>
                <Input placeholder="+971..." value={createForm.customerPhone} onChange={e => setCreateForm(f => ({ ...f, customerPhone: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" placeholder="customer@email.com" value={createForm.customerEmail} onChange={e => setCreateForm(f => ({ ...f, customerEmail: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Watch *</Label>
              <Select value={createForm.listingId} onValueChange={v => setCreateForm(f => ({ ...f, listingId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select a watch" /></SelectTrigger>
                <SelectContent>
                  {listings.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      <span className="flex items-center gap-2">
                        {l.image && <img src={l.image} alt="" className="w-5 h-5 rounded object-cover" />}
                        {l.brand} {l.model} — ${l.price?.toLocaleString()}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Input placeholder="WhatsApp inquiry details, agreed price, etc." value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-start gap-2 pt-2">
              <Button type="submit" disabled={creating} className="bg-primary text-primary-foreground">
                {creating ? 'Creating...' : 'Create Order'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
